import React, { FC, useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'

import { Grid, TextField, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import { toast } from 'react-toastify'
import { UserRequestResetPasswordRequestDto } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import capitalizeFirstLetter from '../../../utils/capitalizeFirstLetter'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { routes } from '../../../constants/routes'
import { rxios } from '../../../utils/NetUtils'

const resolver = classValidatorResolver(UserRequestResetPasswordRequestDto)

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: '25ch',
    },
  },
  main: {
    width: '100%',
    minHeight: 'calc(89vh - 248px)',
  },
  container: {
    borderTop: '1px solid rgba(0,0,0,0.24)',
    maxWidth: 1184,
    margin: '0 auto',
    marginTop: 176,
    paddingTop: 129,
  },
  section: {
    width: 496,
  },
  title: {
    fontFamily: 'Montserrat',
    textAlign: 'left',
    fontSize: 40,
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  form: {
    marginTop: 64,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  input: {
    fontSize: 16,
    marginBottom: 16,
    padding: '8px 0',
    color: theme.palette.success.main,
    fontFamily: 'Montserrat',
  },
  button: {
    fontFamily: 'Montserrat',
    borderRadius: 28,
    minHeight: 56,
    background: theme.palette.success.main,
    marginTop: 32,
  },
  forgetPass: {
    color: '#584DE2',
    fontWeight: 'bold',
    fontSize: 16,
    cursor: 'pointer',
    textAlign: 'left',
  },
  link: {
    textDecoration: 'none',
  },
  errorField: {
    color: 'red',
  },
}))

export const RequestResetPassword: FC = () => {
  const classes = useStyle()
  const formMethods = useForm<UserRequestResetPasswordRequestDto>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver,
  })

  const {
    handleSubmit,
    register,
    formState: { isValid, errors },
  } = formMethods

  const history = useHistory()
  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit = useCallback(
    async (data: UserRequestResetPasswordRequestDto) => {
      setLoading(true)
      const requestData = { ...data, email: data.email.trim() }

      rxios.post(`/user/reset-password/`, requestData).subscribe(
        // eslint-disable-next-line
        (next: any) => {
        },
        // eslint-disable-next-line
        (error: any) => {
          toast.error(error.response.data.message)
          setLoading(false)
        },
        () => {
          toast.success('Password was reseted')
          history.push(routes.LOGIN)
          setLoading(false)
        },
      )
    },
    // eslint-disable-next-line
    [],
  )

  const errorMessage = useCallback(
    (inputName: string) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const error = errors[inputName]
      const message = error?.message.replace(inputName, '')
      return capitalizeFirstLetter(message)
    },
    [errors],
  )

  return (
    <section className={classes.main}>
      <Grid
        container
        item
        xs={6}
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        className={classes.container}
      >
        <Grid className={classes.section}>
          <Typography variant="h2" className={classes.title}>
            Reset Password
          </Typography>

          <form className="root" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Grid container className={classes.form}>
              <TextField
                className={classes.input}
                variant="standard"
                required
                id="standard-basic-name"
                label="Email"
                {...register('email', { required: true })}
              />
              {errors.email && (
                <Typography variant="body1" gutterBottom className={classes.errorField}>
                  {errorMessage('email')}
                </Typography>
              )}

              <GreenButton
                type="submit"
                width="100%"
                mt={32}
                text={'Reset Password'}
                disabled={!isValid || loading}
                noHistory
              />
            </Grid>
          </form>
        </Grid>
      </Grid>
    </section>
  )
}
